import { CdkDragDrop, moveItemInArray } from '@angular/cdk/drag-drop';
import { Component, Inject, OnInit } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { MatSnackBar } from '@angular/material/snack-bar';
import { DEFAULT_SNACKBAR_CONFIG } from 'src/app/@shared/constants/site.constants';
import {
  buildDomainSavePayloadFromType,
  hasDuplicateAllowedValues,
  isDuplicateAllowedValue,
  StoreAttributeTypeViewModel,
  VALUE_TYPE_DIALOG_LABELS,
} from '../../../../models/store-attribute.model';
import { StoreAttributeService } from '../../../../services/store-attribute.service';

export interface ManageValuesDialogData {
  attributeType: StoreAttributeTypeViewModel;
  categoryName: string;
}

interface ManagedValue {
  label: string;
  storeCount: number;
}

@Component({
  selector: 'app-manage-values-dialog',
  templateUrl: './manage-values-dialog.component.html',
  styleUrls: ['./manage-values-dialog.component.scss'],
})
export class ManageValuesDialogComponent implements OnInit {
  values: ManagedValue[] = [];
  newValueInput = '';

  constructor(
    private dialogRef: MatDialogRef<ManageValuesDialogComponent>,
    private storeAttributeService: StoreAttributeService,
    private matSnackBar: MatSnackBar,
    @Inject(MAT_DIALOG_DATA) public data: ManageValuesDialogData,
  ) {}

  ngOnInit(): void {
    this.values = this.data.attributeType.values.map((value) => ({ ...value }));
  }

  get title(): string {
    return `Edit Values - ${this.data.attributeType.name}`;
  }

  get subtitle(): string {
    const valueTypeLabel =
      VALUE_TYPE_DIALOG_LABELS[this.data.attributeType.valueType] ??
      this.data.attributeType.valueType;
    return `Category: ${this.data.categoryName} | Value type: ${valueTypeLabel}`;
  }

  get addValuePlaceholder(): string {
    return `Add new ${this.data.attributeType.name.toLowerCase()} value...`;
  }

  addValue(): void {
    const label = this.newValueInput.trim();
    if (!label) {
      return;
    }
    const existingLabels = this.values.map((value) => value.label);
    if (isDuplicateAllowedValue(existingLabels, label)) {
      this.matSnackBar.open('This value already exists', 'OK', DEFAULT_SNACKBAR_CONFIG);
      return;
    }
    this.values = [...this.values, { label, storeCount: 0 }];
    this.newValueInput = '';
  }

  removeValue(index: number): void {
    this.values = this.values.filter((_, i) => i !== index);
  }

  onValueInputKeydown(event: KeyboardEvent): void {
    if (event.key === 'Enter') {
      event.preventDefault();
      this.addValue();
    }
  }

  drop(event: CdkDragDrop<ManagedValue[]>): void {
    moveItemInArray(this.values, event.previousIndex, event.currentIndex);
  }

  onCancel(): void {
    this.dialogRef.close(false);
  }

  onSave(): void {
    const labels = this.values.map((value) => value.label);
    if (hasDuplicateAllowedValues(labels)) {
      this.matSnackBar.open('Duplicate values are not allowed', 'OK', DEFAULT_SNACKBAR_CONFIG);
      return;
    }

    const payload = buildDomainSavePayloadFromType(this.data.attributeType, {
      Values: labels,
    });

    this.storeAttributeService
      .updateDomainValues(this.data.attributeType.id, payload)
      .subscribe({
        next: () => {
          this.storeAttributeService.reload();
          this.matSnackBar.open('Values updated', 'OK', DEFAULT_SNACKBAR_CONFIG);
          this.dialogRef.close(true);
        },
        error: () => {
          this.storeAttributeService.reload();
          this.matSnackBar.open('Error updating values', 'OK', DEFAULT_SNACKBAR_CONFIG);
        },
      });
  }
}
